/**
 * Perimenopause MA closed-cohort review — client types + helpers.
 *
 * Mirrors the backend: rubric (board/peri_review_rubric.py), cohort assignment
 * (board/peri_review_cohort.py), and the verdict/impression store
 * (board/peri_review.py). The pass/disagreement helpers below are a CLIENT MIRROR
 * of the server derivation (peri_review.primary_metric_pass / disagreements) so
 * the reviewer sees the outcome live before submitting; the server remains the
 * source of truth. Design: docs/superpowers/specs/2026-07-22-peri-review-surface-design.md.
 */

export type PeriReviewMode = 'blinded' | 'open';
export type StrataStage = 'early_transition' | 'late_transition';
export type StrataSubbranch = 'base' | 'nd_affirming';

/** Per-participant review progress, derived from the store on the server. */
export type PeriReviewStatus = 'not_started' | 'impression_recorded' | 'verdict_recorded';

// --- Rubric (the pre-registered, MA-signed scoring rubric) ------------------

export type RubricCategory = {
  id: string;
  label: string;
  prompt: string;
  scale: string[];
  concordant_values: string[];
  note_required_unless?: string[];
  gating?: boolean;
  primary_metric_input?: boolean;
};

export type PrimaryMetric = {
  id: string;
  label: string;
  description?: string;
  pass_rule: Record<string, string[]>;
};

export type PeriReviewRubric = {
  version: number;
  status?: string;
  categories: RubricCategory[];
  primary_metric: PrimaryMetric;
};

// --- Cohort + queue --------------------------------------------------------

export type QueueHeadline = {
  stance: string | null;
  stage: string | null;
  confidence: number | null;
  safety_flag: boolean;
};

export type DisagreementRow = { category: string; value: string; note: string };

/** Present once a participant has been scored (review_status === 'verdict_recorded'). */
export type VerdictSummary = {
  primary_metric_pass: boolean;
  disagreements: DisagreementRow[];
};

export type PeriReviewQueueRow = {
  participant_ref: string;
  review_mode: PeriReviewMode;
  strata_stage: StrataStage;
  strata_subbranch: StrataSubbranch;
  latest_session_id: string | null;
  headline: QueueHeadline;
  review_status: PeriReviewStatus;
  verdict_summary?: VerdictSummary;
};

export type CohortStatus = {
  loaded: boolean;
  size: number;
  blinded: number;
  open: number;
  target: { size_min: number; size_max: number; blinded_min: number; blinded_max: number };
  size_ok: boolean;
  blinded_ok: boolean;
  review_ready: boolean;
  strata_coverage: Record<string, number>;
};

export type PeriReviewQueue = { participants: PeriReviewQueueRow[]; cohort_status: CohortStatus };

// --- Board OUTPUT (revealed only after phase-1 for blinded participants) ----

export type AttributionHypothesis = {
  label: string;
  stance: 'supported' | 'ruled_out' | 'uncertain' | 'needs_data';
  confidence: number;
  reasoning: string;
  supporting_signals?: string[];
};

export type AttributionStaging = {
  stage: string | null;
  stage_label: string | null;
  stage_confidence: number | null;
  basis?: string[];
  limits?: string;
};

export type Attribution = {
  question?: string;
  hypotheses: AttributionHypothesis[];
  staging?: AttributionStaging | null;
  overall_confidence?: number;
  confidence_rationale?: string;
  limits?: string;
  headline_hypothesis_id?: string | null;
};

export type ModeConfig = { mode: string; sub_branch: string; thresholds?: Record<string, unknown> };

export type PeriReviewOutput = {
  attribution: Attribution | null;
  contraindication_flags: Array<Record<string, unknown>>;
  mode_config: ModeConfig;
};

// --- Raw INPUTS bundle (PC-owned reconstruction endpoint) -------------------
// Provisional shape per the contract; finalise against PC's response.
export type PeriReviewInputs = {
  participant_ref: string;
  session_id: string;
  run_started_at: string;
  profile_version: number | null;
  profile: { domain_profiles: Record<string, { key_biomarkers?: unknown[] }> };
  biomarker_histories: Array<{ name: string; unit?: string; readings: Array<{ value: unknown; at: string; source?: string }> }>;
  instrument_captures: Array<{ instrument_id: string; records: Array<{ value?: unknown; score?: unknown; completed_at: string; refers_to?: string }> }>;
  intake_safety: Record<string, unknown>;
  cohort_tags: { review_mode: PeriReviewMode; strata_stage: StrataStage; strata_subbranch: StrataSubbranch };
};

// --- Scoring ---------------------------------------------------------------

export type ScoreEntry = { value: string; note?: string };
export type Scores = Record<string, ScoreEntry>;

export type VerdictRecord = {
  kind: 'verdict';
  participant_ref: string;
  session_id: string;
  reviewer: string;
  signatory: string;
  review_mode: PeriReviewMode;
  rubric_version: number;
  scores: Scores;
  primary_metric_pass: boolean;
  disagreements: Array<{ category: string; value: string; note: string }>;
  timestamp: string;
};

// --- Client-mirror derivation (server is source of truth) ------------------

/** A note is required whenever the value is NOT one the rubric marks benign. */
export function noteRequired(cat: RubricCategory, value: string | undefined): boolean {
  if (!value) return false;
  return !(cat.note_required_unless ?? []).includes(value);
}

/** True when this category's value is one of its concordant values. */
export function isConcordant(cat: RubricCategory, value: string | undefined): boolean {
  return !!value && cat.concordant_values.includes(value);
}

/** Every rubric category scored, each with a value in scale + a note where required. */
export function scoresComplete(rubric: PeriReviewRubric, scores: Scores): boolean {
  return rubric.categories.every((cat) => {
    const entry = scores[cat.id];
    if (!entry || !cat.scale.includes(entry.value)) return false;
    if (noteRequired(cat, entry.value) && !(entry.note ?? '').trim()) return false;
    return true;
  });
}

/** Mirror of peri_review.primary_metric_pass. */
export function computePass(rubric: PeriReviewRubric, scores: Scores): boolean {
  return Object.entries(rubric.primary_metric.pass_rule).every(
    ([cid, allowed]) => allowed.includes(scores[cid]?.value ?? ''),
  );
}

/** Mirror of peri_review.disagreements — non-concordant categories → catalogue rows. */
export function collectDisagreements(
  rubric: PeriReviewRubric,
  scores: Scores,
): Array<{ category: string; value: string; note: string }> {
  const out: Array<{ category: string; value: string; note: string }> = [];
  for (const cat of rubric.categories) {
    const entry = scores[cat.id];
    if (entry?.value && !isConcordant(cat, entry.value)) {
      out.push({ category: cat.id, value: entry.value, note: entry.note ?? '' });
    }
  }
  return out;
}

// --- Slice 6: cohort concordance + disagreement catalogue ------------------

/** The primary accuracy metric across the cohort: proportion of SCORED
 *  participants that pass (no safety issue AND key-findings concordance). */
export function cohortConcordance(rows: PeriReviewQueueRow[]): {
  scored: number; passing: number; pct: number | null;
} {
  const scored = rows.filter((r) => r.verdict_summary);
  const passing = scored.filter((r) => r.verdict_summary!.primary_metric_pass).length;
  return {
    scored: scored.length,
    passing,
    pct: scored.length ? Math.round((passing / scored.length) * 100) : null,
  };
}

/** Every disagreement across scored participants, tagged with the participant —
 *  the catalogue that feeds the backlog. */
export type CatalogueEntry = DisagreementRow & { participant_ref: string };

export function aggregateCatalogue(rows: PeriReviewQueueRow[]): CatalogueEntry[] {
  const out: CatalogueEntry[] = [];
  for (const r of rows) {
    for (const d of r.verdict_summary?.disagreements ?? []) {
      out.push({ ...d, participant_ref: r.participant_ref });
    }
  }
  return out;
}

/** Catalogue grouped by rubric category (category → its disagreement rows). */
export function catalogueByCategory(rows: PeriReviewQueueRow[]): Record<string, CatalogueEntry[]> {
  const grouped: Record<string, CatalogueEntry[]> = {};
  for (const e of aggregateCatalogue(rows)) {
    (grouped[e.category] ??= []).push(e);
  }
  return grouped;
}

// --- Display helpers -------------------------------------------------------

export const REVIEW_STATUS_META: Record<PeriReviewStatus, { label: string; cls: string }> = {
  not_started: { label: 'not started', cls: 'mod-code' },
  impression_recorded: { label: 'impression in', cls: 'mod-json' },
  verdict_recorded: { label: 'scored', cls: 'mod-sign' },
};

export function strataLabel(stage: StrataStage, sub: StrataSubbranch): string {
  const s = stage === 'early_transition' ? 'early' : 'late';
  const b = sub === 'nd_affirming' ? 'ND-affirming' : 'base';
  return `${s} · ${b}`;
}

/** Row matches the free-text filter on ref / strata / mode / status. */
export function matchesRow(row: PeriReviewQueueRow, q: string): boolean {
  if (!q.trim()) return true;
  const hay = [
    row.participant_ref, row.review_mode, row.strata_stage, row.strata_subbranch, row.review_status,
  ].join(' ').toLowerCase();
  return hay.includes(q.trim().toLowerCase());
}
